import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Button } from './ui/button';
import { Input } from './ui/input';
import MentionInput, { RenderMentionText } from './MentionInput';
import { parseUtc } from '../lib/calendarDate';
import {
  MessageCircle, Send, ThumbsUp, Reply, Trash2,
  ChevronDown, ChevronUp, Loader2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* Brief 27 oct A.1 + A.2 (RCA) — ce composant avait son propre état « replié »
   (toggle interne hérité de la version boutique) : un 1er clic sur l'icône
   ouvrait la section, un 2e clic sur « N commentaires » chargeait la liste.
   La liste se charge désormais dès l'affichage ; le champ de saisie occupe
   toute la largeur de la publication, avatar à gauche. */

const formatTime = (dateString) => {
  const date = parseUtc(dateString) || new Date(dateString);
  const diffMins = Math.floor((Date.now() - date) / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffMins < 1) return "À l'instant";
  if (diffMins < 60) return `${diffMins} min`;
  if (diffHours < 24) return `${diffHours} h`;
  if (diffDays < 30) return `${diffDays} j`;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

const UserAvatar = ({ user, size = 'h-9 w-9', onClick }) => (
  <Avatar className={`${size} flex-shrink-0 ${onClick ? 'cursor-pointer' : ''}`} onClick={onClick}>
    <AvatarImage src={user?.avatar} />
    <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white text-xs">
      {user?.display_name?.substring(0, 2).toUpperCase()}
    </AvatarFallback>
  </Avatar>
);

const CommentItem = ({ comment, onReply, onDelete, onLike, currentUserId }) => {
  const navigate = useNavigate();
  const [showReplies, setShowReplies] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [liked, setLiked] = useState(comment.is_liked || false);
  const [likeCount, setLikeCount] = useState(comment.like_count || 0);

  const goProfile = (uid) => uid && navigate(`/app/profile/${uid}`);

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    if (!replyContent.trim()) return;
    setSubmitting(true);
    await onReply(comment.comment_id, replyContent);
    setReplyContent('');
    setShowReplyInput(false);
    setShowReplies(true);
    setSubmitting(false);
  };

  return (
    <div className="group" data-testid={`comment-${comment.comment_id}`}>
      <div className="flex gap-3">
        <UserAvatar user={comment.user} onClick={() => goProfile(comment.user?.user_id)} />
        <div className="flex-1 min-w-0">
          <div className="bg-gray-50 dark:bg-gray-700/70 rounded-2xl px-4 py-2">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm text-gray-900 dark:text-white cursor-pointer hover:underline" onClick={() => goProfile(comment.user?.user_id)}>
                {comment.user?.display_name}
              </span>
              <span className="text-xs text-gray-400 dark:text-gray-500">{formatTime(comment.created_at)}</span>
            </div>
            <p className="text-gray-700 dark:text-gray-200 text-sm mt-0.5 break-words">
              <RenderMentionText text={comment.content} navigate={navigate} />
            </p>
          </div>

          <div className="flex items-center gap-4 mt-1 ml-2 text-xs">
            <button
              onClick={async () => {
                try {
                  await onLike(comment.comment_id);
                  setLiked(!liked);
                  setLikeCount(liked ? likeCount - 1 : likeCount + 1);
                } catch (e) { console.error(e); }
              }}
              className={`font-medium flex items-center gap-1 transition-colors ${liked ? 'text-purple-600 dark:text-purple-400' : 'text-gray-500 dark:text-gray-400 hover:text-purple-500'}`}
              data-testid={`comment-like-${comment.comment_id}`}
            >
              <ThumbsUp className={`h-3 w-3 ${liked ? 'fill-current' : ''}`} />
              {likeCount}
            </button>
            <button
              onClick={() => setShowReplyInput(!showReplyInput)}
              className="text-gray-500 dark:text-gray-400 hover:text-purple-500 font-medium flex items-center gap-1"
              data-testid={`comment-reply-${comment.comment_id}`}
            >
              <Reply className="h-3 w-3" />
              Répondre
            </button>
            {currentUserId === comment.user?.user_id && (
              <button
                onClick={() => onDelete(comment.comment_id)}
                className="text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                aria-label="Supprimer le commentaire"
                data-testid={`comment-delete-${comment.comment_id}`}
              >
                <Trash2 className="h-3 w-3" />
              </button>
            )}
          </div>

          {showReplyInput && (
            <form onSubmit={handleSubmitReply} className="mt-2 flex gap-2">
              <Input
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                placeholder={`Répondre à ${comment.user?.display_name}…`}
                className="flex-1 text-sm h-9 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                autoFocus
                data-testid={`comment-reply-input-${comment.comment_id}`}
              />
              <Button type="submit" size="sm" disabled={!replyContent.trim() || submitting} className="bg-purple-500 hover:bg-purple-600 text-white h-9">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </form>
          )}

          {comment.reply_count > 0 && (
            <button
              onClick={() => setShowReplies(!showReplies)}
              className="text-purple-500 dark:text-purple-400 text-sm font-medium mt-2 ml-2 flex items-center gap-1"
              data-testid={`comment-toggle-replies-${comment.comment_id}`}
            >
              {showReplies ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              {showReplies ? 'Masquer les réponses' : `Voir ${comment.reply_count} réponse${comment.reply_count > 1 ? 's' : ''}`}
            </button>
          )}

          {showReplies && comment.replies && (
            <div className="mt-2 space-y-3 ml-2 pl-3 border-l-2 border-purple-100 dark:border-purple-800/50">
              {comment.replies.map((reply) => (
                <div key={reply.comment_id} className="flex gap-2">
                  <UserAvatar user={reply.user} size="h-7 w-7" onClick={() => goProfile(reply.user?.user_id)} />
                  <div className="flex-1 min-w-0">
                    <div className="bg-gray-50 dark:bg-gray-700/70 rounded-xl px-3 py-1.5">
                      <span className="font-semibold text-gray-900 dark:text-white text-sm">{reply.user?.display_name}</span>
                      <p className="text-gray-700 dark:text-gray-200 text-sm"><RenderMentionText text={reply.content} navigate={navigate} /></p>
                    </div>
                    <span className="text-xs text-gray-400 ml-2">{formatTime(reply.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CommentsSection = ({ postId, productId, initialCount = 0, autoFocus = true }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(initialCount);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const endpoint = postId ? `${API}/posts/${postId}/comments` : `${API}/products/${productId}/comments`;

  const loadComments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(endpoint, { withCredentials: true });
      setComments(response.data.comments || []);
      setTotal(response.data.total || 0);
    } catch (e) {
      console.error('Error loading comments:', e);
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  // A.2 — un seul clic : la liste se charge dès que la section est affichée.
  useEffect(() => { loadComments(); }, [loadComments]);

  const handleSubmitComment = async (e) => {
    e?.preventDefault?.();
    if (!newComment.trim() || !user || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const response = await axios.post(endpoint, { content: newComment }, { withCredentials: true });
      setComments((prev) => [{
        ...response.data,
        user: { user_id: user.user_id, display_name: user.display_name, avatar: user.avatar },
        replies: [],
        reply_count: 0,
      }, ...prev]);
      setTotal((prev) => prev + 1);
      setNewComment('');
    } catch (err) {
      setError("Le commentaire n'a pas pu être publié. Réessaie.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async (parentId, content) => {
    try {
      await axios.post(endpoint, { content, parent_id: parentId }, { withCredentials: true });
      loadComments();
    } catch (err) {
      console.error('Error posting reply:', err);
    }
  };

  const handleDelete = async (commentId) => {
    if (!window.confirm('Supprimer ce commentaire ?')) return;
    try {
      await axios.delete(`${API}/comments/${commentId}`, { withCredentials: true });
      setComments((prev) => prev.filter((c) => c.comment_id !== commentId));
      setTotal((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error deleting comment:', err);
    }
  };

  const handleLike = async (commentId) => {
    await axios.post(`${API}/comments/${commentId}/like`, {}, { withCredentials: true });
  };

  return (
    <div className="border-t border-gray-100 dark:border-gray-700 pt-4" data-testid="comments-section">
      {/* Saisie — pleine largeur de la publication, avatar à gauche (A.1) */}
      {user && (
        <form onSubmit={handleSubmitComment} className="flex items-start gap-3 mb-4" data-testid="comment-form">
          <UserAvatar user={user} />
          <div className="flex-1 min-w-0 relative">
            <MentionInput
              value={newComment}
              onChange={setNewComment}
              placeholder="Écrire un commentaire… (@ pour mentionner)"
              className="border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 dark:text-white rounded-2xl pl-4 pr-12 py-2.5 text-sm focus:border-purple-400 focus:ring-1 focus:ring-purple-400 focus:bg-white dark:focus:bg-gray-700 transition-colors"
              minRows={1}
              maxRows={5}
              data-testid="comment-input"
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmitComment(e); } }}
            />
            <button
              type="submit"
              disabled={!newComment.trim() || submitting}
              className="absolute right-1.5 bottom-1.5 h-8 w-8 rounded-full flex items-center justify-center bg-gradient-to-r from-purple-500 to-pink-500 text-white disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
              aria-label="Publier le commentaire"
              data-testid="comment-submit"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </div>
        </form>
      )}
      {error && <p className="text-xs text-red-500 mb-3 ml-12" data-testid="comment-error">{error}</p>}

      <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-3" data-testid="comments-count">
        <MessageCircle className="h-3.5 w-3.5" />
        {total} commentaire{total !== 1 ? 's' : ''}
      </p>

      {loading ? (
        <div className="flex justify-center py-6" data-testid="comments-loading">
          <Loader2 className="h-5 w-5 animate-spin text-purple-500" />
        </div>
      ) : comments.length === 0 ? (
        <p className="text-center text-sm text-gray-400 dark:text-gray-500 py-3" data-testid="comments-empty">
          Aucun commentaire pour l'instant — lance la discussion !
        </p>
      ) : (
        <div className="space-y-4" data-testid="comments-list">
          {comments.map((comment) => (
            <CommentItem
              key={comment.comment_id}
              comment={comment}
              onReply={handleReply}
              onDelete={handleDelete}
              onLike={handleLike}
              currentUserId={user?.user_id}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CommentsSection;
